import { Database, AlertTriangle, Building2, Globe, FolderOpen, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { KbStats } from '@/hooks/useKnowledgeBase';
import { sourceTypeMeta } from '../kbDisplay';

export interface KbStatsCardProps {
  stats: KbStats | undefined;
  isLoading: boolean;
  className?: string;
}

export function KbStatsCard({ stats, isLoading, className }: KbStatsCardProps) {
  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">Knowledge Base Statistics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-12" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  // Optional fields are omitted when the backend does not provide them
  // (StatsResponse only guarantees totals and breakdowns).
  const statItems = [
    {
      icon: Database,
      label: 'Total Entries',
      value: stats.total_entries.toLocaleString(),
      color: 'text-blue-600',
    },
    {
      icon: AlertTriangle,
      label: 'Stale Entries',
      value: stats.stale_entries.toLocaleString(),
      color: stats.stale_entries > 0 ? 'text-amber-600' : 'text-green-600',
    },
    ...(stats.firm_specific_entries != null
      ? [
          {
            icon: Building2,
            label: 'Firm-Specific',
            value: stats.firm_specific_entries.toLocaleString(),
            color: 'text-purple-600',
          },
        ]
      : []),
    ...(stats.global_entries != null
      ? [
          {
            icon: Globe,
            label: 'Global Entries',
            value: stats.global_entries.toLocaleString(),
            color: 'text-teal-600',
          },
        ]
      : []),
    ...(stats.recent_updates != null
      ? [
          {
            icon: Clock,
            label: 'Recent Updates',
            value: stats.recent_updates.toLocaleString(),
            color: 'text-gray-600',
            subtitle: 'Last 7 days',
          },
        ]
      : []),
  ];

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <Database className="h-4 w-4" />
          Knowledge Base Statistics
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main Stats */}
        <div className="space-y-3">
          {statItems.map((item) => (
            <div key={item.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <item.icon className={cn('h-4 w-4', item.color)} />
                <span className="text-sm text-muted-foreground">{item.label}</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium">{item.value}</span>
                {item.subtitle && (
                  <span className="text-xs text-muted-foreground ml-1">({item.subtitle})</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Category Breakdown */}
        {Object.keys(stats.by_category).length > 0 && (
          <div className="pt-3 border-t">
            <div className="flex items-center gap-2 mb-2">
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">By Category</span>
            </div>
            <div className="space-y-1.5">
              {Object.entries(stats.by_category)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
                .map(([category, count]) => (
                  <div key={category} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground truncate max-w-[140px]" title={category}>
                      {category || 'Uncategorized'}
                    </span>
                    <span className="font-medium tabular-nums">{count.toLocaleString()}</span>
                  </div>
                ))}
              {Object.keys(stats.by_category).length > 5 && (
                <div className="text-xs text-muted-foreground text-center pt-1">
                  +{Object.keys(stats.by_category).length - 5} more categories
                </div>
              )}
            </div>
          </div>
        )}

        {/* Source Type Breakdown */}
        <div className="pt-3 border-t">
          <div className="text-sm font-medium mb-2">By Source Type</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.by_source_type)
              .filter(([, count]) => count > 0)
              .map(([sourceType, count]) => (
                <div
                  key={sourceType}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-xs"
                >
                  <span className="text-muted-foreground">
                    {sourceTypeMeta(sourceType).label}:
                  </span>
                  <span className="font-medium">{count.toLocaleString()}</span>
                </div>
              ))}
          </div>
        </div>

        {/* Avg Response Length (only when backend provides it) */}
        {stats.avg_response_length != null && (
          <div className="pt-3 border-t text-sm">
            <span className="text-muted-foreground">Avg Response Length:</span>{' '}
            <span className="font-medium">{Math.round(stats.avg_response_length)} chars</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
